import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { InventorySource } from 'types/inventory';
import styles from './InventorySourceSearchInput.module.scss';

interface InventorySourceSearchInputProps {
  sources: InventorySource[];
  value: string; // source ID
  onChange: (sourceId: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

function InventorySourceSearchInput({
  sources,
  value,
  onChange,
  placeholder = 'Search for an inventory source...',
  required = false,
  disabled = false,
}: InventorySourceSearchInputProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  // Get selected source for display
  const selectedSource = value ? sources.find((s) => s.id === value) : null;
  const displayValue = selectedSource ? selectedSource.alias : searchQuery;

  // Filter sources based on search query
  const filteredSources = sources.filter((source) => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        source.alias.toLowerCase().includes(query) ||
        source.category.name.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Handle input focus
  const handleFocus = () => {
    setIsOpen(true);
    if (!value) {
      setSearchQuery('');
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Clear selection if user is typing
    if (value && query !== selectedSource?.alias) {
      onChange('');
    }
  };

  // Handle source selection
  const handleSelect = (sourceId: string) => {
    onChange(sourceId);
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle clear button
  const handleClear = () => {
    onChange('');
    setSearchQuery('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredSources.length === 0) {
      if (e.key === 'Enter' && required) {
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSources.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredSources.length
        ) {
          handleSelect(filteredSources[highlightedIndex].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (
      highlightedIndex >= 0 &&
      suggestionsRef.current &&
      suggestionsRef.current.children[highlightedIndex]
    ) {
      suggestionsRef.current.children[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.inputWrapper}>
        <Search size={16} className={styles.searchIcon} />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={styles.input}
          autoComplete="off"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear selection"
          >
            <X size={16} />
          </button>
        )}
      </div>
      {isOpen && filteredSources.length > 0 && (
        <ul className={styles.suggestions} ref={suggestionsRef}>
          {filteredSources.map((source, index) => (
            <li
              key={source.id}
              className={`${styles.suggestionItem} ${
                index === highlightedIndex ? styles.highlighted : ''
              } ${value === source.id ? styles.selected : ''}`}
              onClick={() => handleSelect(source.id)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className={styles.suggestionAlias}>{source.alias}</div>
              <div className={styles.suggestionCategory}>
                {source.category.name}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default InventorySourceSearchInput;
